import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { partial } from 'lodash';
import { HandCoins, MessageSquare, PanelLeft, Users, X } from 'lucide-react';
import { useLayout } from '../../contexts/layout-context';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTitle } from '../ui/sheet';
import FeedbackModal from '../feedback/feedback-modal';

// Icons
import NoNameLogo from "../../assets/images/brand-logo.svg?react";
import SurveyIcon from "../../assets/icons-redesign/chat-bubble.svg?react";
import Download from "../../assets/icons-redesign/download.svg?react";
import BarChart from "../../assets/icons-redesign/bar-chart.svg?react";
import { isClientMember, isOrgMember } from 'src/services/permission/permission.helpers';
import { isClientUrl, isOrgUrl } from 'src/services/permission/permission.helpers';
import { getUserStateActiveClient, getIsUserStaff } from 'src/redux/user/user.selectors';
import { Avatar } from 'primereact/avatar';
import { Client } from 'src/pages/client-management/types/client.types';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { OPERATIONS_SIDEBAR } from './operations-nav.config';

const ORG_USER_SIDEBAR_BUTTONS = [
  {
    permissions: [],
    title: 'Surveys',
    subtitle: 'Create, generate, or build surveys using AI or templates',
    color: 'bg-blue-500',
    icon: <SurveyIcon className="size-[18px] fill-white" />,
    path: '/surveys',
  },
  {
    permissions: [],
    title: 'Reports',
    subtitle: 'Generate insights from ad campaign data',
    color: 'bg-green-501',
    icon: <Download className="size-[18px] stroke-white" />,
    path: '/reports',
  },
  {
    permissions: [],
    title: 'Billing',
    subtitle: 'Generate insights from ad campaign data',
    color: 'bg-yellow-500',
    icon: <HandCoins className="size-[18px] stroke-white" />,
    path: '/billing',
  },
  {
    permissions: [],
    title: 'Performance',
    subtitle: 'Monitor and analyze system performance metrics',
    color: 'bg-orange-500',
    icon: <BarChart className="size-[18px] stroke-white" />,
    path: '/performance',
  },
  {
    permissions: [],
    title: 'Mange Clients',
    subtitle: 'Manage clients and their data',
    color: 'bg-red-502',
    icon: <Users className="size-[18px] fill-white" />,
    path: '/manage-clients',
  },
  {
    permissions: [],
    title: OPERATIONS_SIDEBAR.title,
    subtitle: OPERATIONS_SIDEBAR.subtitle,
    color: OPERATIONS_SIDEBAR.color,
    icon: OPERATIONS_SIDEBAR.icon,
    path: OPERATIONS_SIDEBAR.path,
  },
];

const CLIENT_USER_SIDEBAR_BUTTONS = [
  {
    key: 'surveys',
    permissions: [],
    title: 'Surveys',
    subtitle: 'Create, generate, or build surveys using AI or templates',
    color: 'bg-blue-500',
    icon: <SurveyIcon className="size-[18px] fill-white" />,
    path: '/client/:clientId/surveys',
  },
  {
    key: 'reporting',
    permissions: [],
    title: 'Reports',
    subtitle: 'Generate insights from ad campaign data',
    color: 'bg-green-501',
    icon: <Download className="size-[18px] mt-[-2px] ml-[-1px] stroke-white" />,
    path: '/client/:clientId/reports-old',
  },
  {
    key: 'performance',
    permissions: [],
    title: 'Performance',
    subtitle: 'Generate insights from ad campaign data',
    color: 'bg-yellow-500',
    icon: <BarChart className="size-[18px] stroke-white" />,
    path: '/client/:clientId/performance',
  },
];

const getSidebarButtons = (
  userPermissions: any,
  activeClient: Client | null,
  isStaff: boolean,
) => {
  // all routes
  if (!userPermissions) {
    return [];
  }

  // all org based routes
  if (isOrgUrl()) {
    if (isOrgMember(userPermissions)) {
      return ORG_USER_SIDEBAR_BUTTONS.filter((button) => !button.staffOnly || isStaff);
    }
  }

  // all client based routes
  if (isClientUrl()) {
    if (isClientMember(userPermissions) || isOrgMember(userPermissions)) {
      if (activeClient) {
        return CLIENT_USER_SIDEBAR_BUTTONS.filter((button) => activeClient.permissions.some((permission) => permission.page === button.key));
      }
      return [];
    }
  }

  return [];
};


interface AppSidebarButtonProps {
  readonly show?: boolean;
  readonly onClick: () => void;
  readonly title: string;
  readonly subtitle: string;
  readonly icon: React.ReactNode;
  readonly color?: string;
  readonly isActive?: boolean;
}

const AppSidebarButton: React.FC<AppSidebarButtonProps> = ({
  show = true,
  onClick,
  subtitle,
  title,
  icon,
  color = 'bg-blue-500',
  isActive = false,
}) => {
  const { sidebarCollapsed } = useLayout();

  if (!show) {
    return null;
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>

        <TooltipContent side='right'>
          {title}
        </TooltipContent>
        <TooltipTrigger asChild>
          <div
            className={`flex gap-3 p-3 rounded-3xl cursor-pointer transition-all duration-200 ${
              isActive ? 'bg-gray-300' : 'hover:bg-gray-200'
            } ${sidebarCollapsed ? 'justify-center' : ''}`}
            onClick={onClick}
            title={sidebarCollapsed ? title : undefined}
          >

            <div className={`flex shrink-0 size-8 rounded-full items-center justify-center self-center overflow-hidden ${color}`}>
              {icon}
            </div>


            {!sidebarCollapsed && (
              <div className="min-w-0 flex-1">
                <p className="font-bold text-sm leading-tight">
                  {title}
                </p>
                <p className="text-xs text-gray-600 leading-tight">
                  {subtitle}
                </p>
              </div>
            )}
          </div>
        </TooltipTrigger>
      </Tooltip>
    </TooltipProvider>
  );
};

interface AppSidebarProps {
  readonly activePage?: string;
}

const AppSidebarDesktop: React.FC<AppSidebarProps> = ({ activePage }) => {
  const userPermissions = useSelector((state: any) => state.userState.permissions);
  const isStaff = useSelector(getIsUserStaff);
  const activeClient = useSelector(getUserStateActiveClient);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId?: string }>();
  const { sidebarCollapsed, toggleSidebar } = useLayout();

  const sidebarButtons: any[] = getSidebarButtons(userPermissions, activeClient, isStaff);

  const navTo = (path: string) => {
    if (clientId) {
      path = path.replace(':clientId', clientId);
    }
    // if (path.includes('client')) {
    //   navigate(`/client/${activeClient.ext_client_id}/dashboard`);
    // }
    navigate(path);
  };

  return (
    <div
      className={`fixed left-0 top-0 h-full bg-sidebar border-r border-gray-200 transition-all duration-300 z-40 ${
        sidebarCollapsed ? 'w-16' : 'w-80'
      }`}
    >
      {/* Header */}
      <div
        className={`flex items-center border-b border-gray-200 h-16 ${
          sidebarCollapsed ? 'justify-center px-2' : 'justify-between px-4'
        }`}
      >
        <Button
          className="size-8 p-0 shrink-0"
          onClick={partial(navTo, '/surveys')}
        >
          {activeClient ? (
            <Avatar
              image={activeClient.brand ?? ''}
              label={activeClient.title.slice(0, 2).toUpperCase() ?? ''}
              className="size-8 bg-gray-500"
            />
          ) : (
            <Avatar className="size-8 bg-black rounded-full">
              <NoNameLogo className="h-[18px] fill-white" />
            </Avatar>
          )}
        </Button>
        {!sidebarCollapsed && (
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="h-8 w-8"
          >
            <PanelLeft className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Content */}
      <div
        className={`flex flex-col gap-3 p-3 h-[calc(100%-4rem)] overflow-y-auto ${
          sidebarCollapsed ? 'items-center' : ''
        }`}
      >
        {sidebarButtons.map((button) => (
          <AppSidebarButton
            show={button.permissions.length > 0 ? button.permissions.includes(userPermissions) : true}
            key={button.title}
            title={button.title}
            subtitle={button.subtitle}
            color={button.color}
            icon={button.icon}
            onClick={partial(navTo, button.path)}
            isActive={
              activePage === button.path
              || (button.path === OPERATIONS_SIDEBAR.path && activePage?.startsWith('/operations'))
            }
          />
        ))}

        <div className="mt-auto pt-4 space-y-3">
          <AppSidebarButton
            title="Feedback"
            subtitle="Report bugs or suggest improvements"
            color="bg-gray-600"
            icon={<MessageSquare className="size-[18px] stroke-white" />}
            onClick={() => setIsFeedbackModalOpen(true)}
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="h-8 w-8"
          >
            <PanelLeft className="h-4 w-4" />
          </Button>
        </div>

      </div>

      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
      />
    </div>
  );
};

const AppSidebarMobile: React.FC<AppSidebarProps> = ({ activePage }) => {
  const navigate = useNavigate();
  const { mobileSidebarOpen, setMobileSidebarOpen } = useLayout();
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  const activeClient = useSelector(getUserStateActiveClient);
  const userPermissions = useSelector((state: any) => state.userState.permissions);
  const isStaff = useSelector(getIsUserStaff);
  const sidebarButtons: any[] = getSidebarButtons(userPermissions, activeClient, isStaff);

  const navTo = (path: string) => {
    navigate(path);
    setMobileSidebarOpen(false); // Close sidebar after navigation
  };

  return (
    <Sheet
      open={mobileSidebarOpen}
      onOpenChange={setMobileSidebarOpen}
    >
      <SheetTitle className="hidden" />
      <SheetContent
        side="left"
        className="w-80 p-0 bg-sidebar [&>button]:hidden"
        aria-describedby={undefined}
      >
        <div className="flex h-full w-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 h-16 px-4">
            <Button
              className="size-8 p-0"
              onClick={partial(navTo, '/surveys')}
            >
              {activeClient ? (
                <Avatar
                  image={activeClient.brand ?? ''}
                  label={activeClient.title.slice(0, 2).toUpperCase() ?? ''}
                  className="size-8 bg-gray-500"
                />
              ) : (
                <Avatar className="size-8 bg-black rounded-full">
                  <NoNameLogo className="h-[18px] fill-white" />
                </Avatar>
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileSidebarOpen(false)}
              className="h-8 w-8"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex flex-col gap-3 p-4 flex-1 overflow-y-auto">

            {sidebarButtons.map((button) => (
              <AppSidebarButton
                show={button.permissions.length > 0 ? button.permissions.includes(userPermissions) : true}
                key={button.title}
                title={button.title}
                subtitle={button.subtitle}
                color={button.color}
                icon={button.icon}
                onClick={partial(navTo, button.path)}
                isActive={
                  activePage === button.path
                  || (button.path === OPERATIONS_SIDEBAR.path && activePage?.startsWith('/operations'))
                }
              />
            ))}

            <div className="mt-auto pt-4">
              <AppSidebarButton
                title="Feedback"
                subtitle="Report bugs or suggest improvements"
                color="bg-gray-600"
                icon={<MessageSquare className="size-[18px] stroke-white" />}
                onClick={() => {
                  setIsFeedbackModalOpen(true);
                  setMobileSidebarOpen(false);
                }}
              />
            </div>
          </div>
        </div>

        <FeedbackModal
          isOpen={isFeedbackModalOpen}
          onClose={() => setIsFeedbackModalOpen(false)}
        />
      </SheetContent>
    </Sheet>
  );
};

export const AppSidebar: React.FC<AppSidebarProps> = ({ activePage }) => {
  const { isMobile } = useLayout();

  return (
    <>
      {isMobile ? (
        <AppSidebarMobile activePage={activePage} />
      ) : (
        <AppSidebarDesktop activePage={activePage} />
      )}
    </>
  );
};